import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import Sidebar from "../../../components/sidebar/Sidebar";
import LoadingSpinner from "../../../components/LoadingSpinner";
import "../../accounts/view/ViewAccountPage.css";
import { MUI_TABLE } from '../../../utils/muiTableConfig';
import { Username, useMask } from '../../../utils/privacyMask';


const fmtDate  = ms => ms ? new Date(Number(ms)).toLocaleDateString('en-GB') : '—';
const fmtDatetime = ms => ms ? new Date(Number(ms)).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

export default function ViewPointsPage() {
  const { discordId } = useParams();
  const navigate = useNavigate();
  const { mask } = useMask();

  const [record, setRecord] = useState(null);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]    = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    (async () => {
      try {
        const [recRes, claimsRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/referralpoints/discord/${discordId}`, { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/referralpoints/claims/${discordId}`, { headers }),
        ]);
        setRecord(recRes.data);
        setClaims(claimsRes.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load points data');
      } finally {
        setLoading(false);
      }
    })();
  }, [discordId]);

  const claimColumns = useMemo(() => [
    {
      header: 'Claimed At',
      accessorKey: 'claimedAt',
      Cell: ({ cell }) => <span style={{ color: '#94a3b8' }}>{fmtDatetime(cell.getValue())}</span>,
    },
    {
      header: 'Reward Type',
      accessorKey: 'rewardType',
      Cell: ({ cell }) => <span style={{ fontWeight: 600 }}>{cell.getValue()}</span>,
    },
    {
      header: 'Reward Label',
      accessorKey: 'rewardLabel',
      Cell: ({ cell }) => <span style={{ color: '#94a3b8' }}>{cell.getValue() || '—'}</span>,
    },
    {
      header: 'Points Cost',
      accessorKey: 'pointsCost',
      Cell: ({ cell }) => (
        <span style={{ color: '#f87171', fontWeight: 600 }}>
          <i className="bi bi-dash-circle" style={{ fontSize: '0.7rem', marginRight: 4 }} />
          {mask(cell.getValue()?.toLocaleString(), 'number')}
        </span>
      ),
    },
  ], [mask]);

  const claimsTable = useMaterialReactTable({
    columns: claimColumns,
    data: claims ?? [],
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGlobalFilter: true,
    enableRowActions: false,
    initialState: { showGlobalFilter: false },
    ...MUI_TABLE,
  });

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <>
        <Sidebar title="Points" />
        <div className="view-page">
          <div style={{ padding: '12px 16px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 10, color: '#f87171', fontSize: '0.875rem' }}>
            <i className="bi bi-exclamation-triangle-fill me-2" />{error}
          </div>
        </div>
      </>
    );
  }

  const spent = (record.totalPointsEarned ?? 0) - (record.availablePoints ?? 0);

  return (
    <>
      <Sidebar title="Points" />

      <div className="view-page">

        {/* ── Header ── */}
        <div className="view-header">
          <div className="view-header-left">
            <div className="view-avatar" style={{ background: 'linear-gradient(135deg, #7c3aed, #a78bfa)' }}>
              <i className="bi bi-discord" />
            </div>
            <div>
              <h1 className="view-title">{record.discordUsername ? <Username value={record.discordUsername} /> : 'Unknown User'}</h1>
              <div className="view-subtitle" style={{ fontFamily: 'monospace' }}>ID: {record.discordId}</div>
            </div>
          </div>
          <Link
            to={`/points/add?discordId=${record.discordId}&username=${encodeURIComponent(record.discordUsername || '')}`}
            className="btn-edit-account"
          >
            <i className="bi bi-star-fill" /> Adjust Points
          </Link>
        </div>

        {/* ── Stat cards ── */}
        <div className="view-stats">
          <div className="view-stat-card" style={{ '--accent': '#a78bfa', '--glow': 'rgba(167,139,250,0.25)' }}>
            <div className="view-stat-label">Total Earned</div>
            <div className="view-stat-value">{mask(record.totalPointsEarned?.toLocaleString() ?? 0, 'number')}</div>
            <i className="bi bi-star-fill view-stat-icon" />
          </div>
          <div className="view-stat-card" style={{ '--accent': '#34d399', '--glow': 'rgba(52,211,153,0.25)' }}>
            <div className="view-stat-label">Available</div>
            <div className="view-stat-value">{mask(record.availablePoints?.toLocaleString() ?? 0, 'number')}</div>
            <i className="bi bi-wallet2 view-stat-icon" />
          </div>
          <div className="view-stat-card" style={{ '--accent': '#f87171', '--glow': 'rgba(248,113,113,0.25)' }}>
            <div className="view-stat-label">Spent</div>
            <div className="view-stat-value">{mask(spent.toLocaleString(), 'number')}</div>
            <i className="bi bi-gift view-stat-icon" />
          </div>
          <div className="view-stat-card" style={{ '--accent': '#60a5fa', '--glow': 'rgba(96,165,250,0.25)' }}>
            <div className="view-stat-label">Total Claims</div>
            <div className="view-stat-value">{claims.length}</div>
            <i className="bi bi-receipt view-stat-icon" />
          </div>
        </div>

        {/* ── Info grid ── */}
        <div className="view-panel" style={{ marginBottom: 20 }}>
          <div className="view-panel-header">
            <h6 className="view-panel-title">
              <i className="bi bi-info-circle" style={{ color: '#a78bfa' }} />
              Details
            </h6>
          </div>
          <div className="info-grid">
            <div className="info-item">
              <div className="info-label"><i className="bi bi-discord" />Discord Username</div>
              <div className="info-value">{record.discordUsername ? <Username value={record.discordUsername} /> : <span className="muted">Not set</span>}</div>
            </div>
            <div className="info-item">
              <div className="info-label"><i className="bi bi-hash" />Discord ID</div>
              <div className="info-value" style={{ fontFamily: 'monospace' }}>{record.discordId}</div>
            </div>
            <div className="info-item">
              <div className="info-label"><i className="bi bi-calendar-plus" />Created</div>
              <div className="info-value">{fmtDate(record.createdAt)}</div>
            </div>
            <div className="info-item">
              <div className="info-label"><i className="bi bi-pencil-square" />Last Updated</div>
              <div className="info-value">{fmtDate(record.updatedAt)}</div>
            </div>
          </div>
        </div>

        {/* ── Claims table ── */}
        <div className="view-panel">
          <div className="view-panel-header">
            <h6 className="view-panel-title">
              <i className="bi bi-receipt" style={{ color: '#60a5fa' }} />
              Claim History
            </h6>
            <span className="view-panel-count">{claims.length}</span>
          </div>
          {claims.length === 0 ? (
            <div style={{ padding: '32px 20px', textAlign: 'center', color: '#334155', fontSize: '0.875rem' }}>
              <i className="bi bi-inbox" style={{ fontSize: '1.8rem', display: 'block', marginBottom: 10 }} />
              No rewards claimed yet
            </div>
          ) : (
            <MaterialReactTable table={claimsTable} />
          )}
        </div>

      </div>
    </>
  );
}
